// Terrain palette — fully synthesized, no original Bolo art bundled.
// Indexed by @bolo2k/shared Terrain enum values.

import { MAX_PLAYERS, Terrain } from '@bolo2k/shared';

export const TERRAIN_COLOR: readonly string[] = [
  '#6b4f2c', // Building — adobe brown
  '#3a6ea5', // River — medium blue
  '#7d6638', // Swamp — muddy ochre
  '#383532', // Crater — dark grey-brown
  '#4d4d4d', // Road — asphalt grey
  '#1f4a1f', // Forest — dark green
  '#5a5a5a', // Rubble — light grey
  '#467f3a', // Grass — meadow green
  '#8a6f4f', // HalfBuilding — lighter adobe
  '#5e8fb8', // Boat — pale blue
  '#0e2840', // DeepSea — navy
  '#c0392b', // RefBase — red placeholder (owner-tinted at runtime)
  '#a0a0a0', // Pillbox — concrete grey (owner-tinted at runtime)
  '#222', // Unknown
];

// Player colour palette — one hue per owner slot. Owner 0xFF is "neutral".
// Length must equal MAX_PLAYERS; the assert below tripwires future cap
// changes. 16 hues, picked to stay distinguishable across the canvas
// background and through the WinBolo tank-sprite tinting (which preserves
// hue but darkens / desaturates the upper rows). Slots 8-15 are darker /
// muted variants so they don't read as duplicates of 0-7.
const PLAYER_HUES: readonly string[] = [
  '#58a6ff', // 0 blue
  '#f0883e', // 1 orange
  '#56d364', // 2 green
  '#e3b341', // 3 amber
  '#d2a8ff', // 4 violet
  '#ff7b72', // 5 red
  '#39d0d8', // 6 teal
  '#ec6cb9', // 7 pink
  '#1f6feb', // 8 deep blue
  '#bc4c00', // 9 burnt orange
  '#26803a', // 10 dark green
  '#9e6a03', // 11 dark amber
  '#8957e5', // 12 dark violet
  '#cf222e', // 13 crimson
  '#1b7c83', // 14 dark teal
  '#bf3989', // 15 magenta
];
if (PLAYER_HUES.length !== MAX_PLAYERS) {
  throw new Error(`PLAYER_HUES length (${PLAYER_HUES.length}) must match MAX_PLAYERS (${MAX_PLAYERS})`);
}

export function ownerColor(owner: number): string {
  if (owner === 0xff) return '#9aa3ad'; // neutral
  if (owner < 0 || owner >= PLAYER_HUES.length) return '#9aa3ad';
  return PLAYER_HUES[owner]!;
}

// A semi-transparent variant for tinting base/pillbox tiles.
export function ownerTint(owner: number, alpha: number): string {
  const c = ownerColor(owner);
  // Convert hex to rgba.
  const r = parseInt(c.slice(1, 3), 16);
  const g = parseInt(c.slice(3, 5), 16);
  const b = parseInt(c.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Generic colours
export const SHELL_COLOR = '#f0f0d0';
export const MINE_VISIBLE_COLOR = '#f85149';
export const LGM_COLOR = '#56d364';
export const HEALTH_GOOD = '#56d364';
export const HEALTH_LOW = '#f85149';

// Terrain that should also paint its tile with a "mined" indicator overlay
// (for mines visible to the local player). Other tiles render without it.
export const MINE_OVERLAY_COLOR = 'rgba(248, 81, 73, 0.5)';

export function terrainColor(terrainCode: number): string {
  return TERRAIN_COLOR[terrainCode] ?? '#222';
}

void Terrain; // imported for documentation cross-reference
